import TaskTemplateTasksDisplay from "@/components/Forms/TaskTemplateTasksDisplay.tsx";
import TaskTemplatesAutocomplete from "@/components/Forms/TaskTemplatesAutocomplete.tsx";
import { addCaseTemplateSchema } from "@/components/Forms/schema/caseTemplateSchema.ts";
import { useEmployee } from "@/contexts/EmployeeProvider.tsx";
import { useAddCaseTemplate } from "@/mutations/useAddCaseTemplate.tsx";
import { useFetchCaseTemplate } from "@/queries/useFetchCaseTemplate.ts";
import { getErrorMessage } from "@/utils/api.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
    Alert,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2 as Grid,
} from "@mui/material";
import { RhfTextField } from "mui-rhf-integration";
import { enqueueSnackbar } from "notistack";
import { type BaseSyntheticEvent, type ReactNode, useEffect, useState } from "react";
import { FormProvider, type SubmitHandler, useForm } from "react-hook-form";
import type { z } from "zod";

export type AddCaseTemplateFormValues = z.infer<typeof addCaseTemplateSchema>;

type Props = {
    showAddButton?: boolean;
    editCaseTemplateId?: string;
    clearEditCaseTemplateId?: () => void;
};

const AddEditCaseTemplateDialog = ({
    showAddButton = true,
    editCaseTemplateId,
    clearEditCaseTemplateId,
}: Props): ReactNode => {
    const { hasPermission } = useEmployee();
    const addCaseTemplate = useAddCaseTemplate(editCaseTemplateId);
    const [open, setOpen] = useState(false);

    const { data, isError, isRefetching, isLoading } = useFetchCaseTemplate({
        caseTemplateUUID: editCaseTemplateId ?? null,
        isQueryEnabled: editCaseTemplateId !== undefined,
    });

    const form = useForm<AddCaseTemplateFormValues>({
        resolver: zodResolver(addCaseTemplateSchema),
        defaultValues: {
            name: "",
            description: "",
            taskTemplates: [],
        },
    });

    const handleSubmit = (values: AddCaseTemplateFormValues) => {
        addCaseTemplate
            .mutateAsync({
                ...values,
            })
            .then(() => {
                enqueueSnackbar("Case Template added.", { variant: "success" });
                handleClose();
            })
            .catch((error) => {
                console.info("error", getErrorMessage(error));
                enqueueSnackbar("Error: Add Case Template failed.", { variant: "error" });
            });
    };

    const handleClose = () => {
        setOpen(false);

        form.reset(form.formState.defaultValues);

        if (clearEditCaseTemplateId) {
            clearEditCaseTemplateId();
        }
    };

    const onSubmit: SubmitHandler<AddCaseTemplateFormValues> = async (
        data: AddCaseTemplateFormValues,
        event?: BaseSyntheticEvent,
    ) => {
        event?.stopPropagation();
        handleSubmit(data);
    };

    useEffect(() => {
        if (editCaseTemplateId && data) {
            form.reset(data);
            setOpen(true);
            return;
        }

        setOpen(false);
    }, [data, editCaseTemplateId]);

    if (!hasPermission("create:case-template")) {
        return null;
    }

    return (
        <>
            {showAddButton && (
                <Button
                    variant={"contained"}
                    sx={{
                        ml: 2,
                        minWidth: "120px",
                    }}
                    onClick={() => setOpen(true)}
                >
                    New Case Template
                </Button>
            )}
            <Dialog open={open} maxWidth="sm" fullWidth>
                <FormProvider {...form}>
                    <form>
                        <DialogTitle>
                            {editCaseTemplateId ? "Edit" : "New"} Case Template
                        </DialogTitle>
                        <DialogContent dividers>
                            {(isLoading || isRefetching) && <CircularProgress />}
                            {isError && <Alert severity={"error"}>Error loading form data.</Alert>}
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 12 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"name"}
                                        label={"Case Template Name"}
                                        sx={{
                                            width: "100%",
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12 }}>
                                    <RhfTextField
                                        control={form.control}
                                        name={"description"}
                                        label={"Case Template Description"}
                                        sx={{
                                            width: "100%",
                                        }}
                                        multiline
                                        rows={4}
                                    />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }} sx={{ pt: 1 }}>
                                    <TaskTemplatesAutocomplete form={form} />
                                </Grid>
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <TaskTemplateTasksDisplay form={form} />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => handleClose()}>Close</Button>
                            <Button variant={"contained"} onClick={form.handleSubmit(onSubmit)}>
                                {editCaseTemplateId ? "Edit" : "Add"} Case Template
                            </Button>
                        </DialogActions>
                    </form>
                </FormProvider>
            </Dialog>
        </>
    );
};

export default AddEditCaseTemplateDialog;
